import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaBars, FaTimes, FaSearch } from "react-icons/fa"; // For menu toggle icons
import { getImagePath } from "../utils/imageUtils";
import { ImFacebook2 } from "react-icons/im";
import { IoLogoYoutube } from "react-icons/io5";
import ModalComponent from "../components/ModalComponent";
import { ContactData } from "../data-types/api-client/api-contacts";

interface MenuItem {
  label: string;
  path: string;
}

interface MenuBarProps {
  items: MenuItem[];
  contact: ContactData | undefined;
}

const MenuBar: React.FC<MenuBarProps> = ({ items, contact }) => {
  const location = useLocation();

  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    // Update selected item based on the current URL
    setSelectedItem(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"; // Disable scrolling
    } else {
      document.body.style.overflow = "auto"; // Enable scrolling
    }

    // Cleanup function to reset on component unmount
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  return (
    <>
      <nav className="bg-custom-white font-semibold text-custom-atrament flex justify-between items-center h-28 md:px-10 sm:px-6 px-2">
        {/* Logo */}
        <div className="flex justify-center items-center ml-2">
          <a href="/">
            <img
              src={getImagePath("images/logo-rentkos.png")}
              alt="Logo"
              className="h-12 w-48 object-cover"
            />
          </a>
        </div>

        {/* Menu Toggle Icon */}
        <div
          className={`mr-4 w-12 h-12 flex justify-center ${
            isOpen
              ? "bg-custom-white text-custom-atrament z-50"
              : "bg-custom-atrament text-custom-white custom-md:hidden"
          }`}
        >
          <button onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        </div>

        {/* Navigation Links for mobile screen*/}
        {isOpen && (
          <div className="flex overflow-auto text-custom-white font-semibold text-2xl bg-custom-atrament flex-col fixed inset-0  pt-32 pl-10 z-40 space-y-6">
            {items.map((item) => (
              <div>
                <Link
                  key={item.label}
                  to={item.path}
                  className={`px-4 py-2 hover:text-custom-red ${
                    selectedItem === item.path
                      ? "text-custom-red"
                      : "text-custom-white"
                  }`}
                  onClick={() => {
                    setSelectedItem(item.path);
                    setIsOpen(false);
                  }}
                >
                  {item.label}
                </Link>
              </div>
            ))}
            <div className="flex self-start items-center justify-center text-center">
              <button
                onClick={openModal}
                className="flex h-[2.7rem] text-lg font-semibold hover:bg-custom-red-light hover:text-custom-atrament items-center rounded-sm border-custom-red bg-custom-red text-custom-white font-roboto px-4 py-1 transition duration-200 ease-in-out"
              >
                REZERVOVAŤ
              </button>
            </div>

            <div className="flex flex-row items-center h-full pb-6 pl-6  space-x-6">
              {contact?.contact.facebookUrl && (
                <a
                  href={`${contact?.contact.facebookUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="md:hover:text-custom-red self-end"
                >
                  <ImFacebook2 size={43} />
                </a>
              )}

              {contact?.contact.youtubeUrl && (
                <a
                  href={`${contact?.contact.youtubeUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="md:hover:text-custom-red self-end"
                >
                  <IoLogoYoutube size={42} />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Navigation Links for bigger screen*/}
        <div
          className={`${
            isOpen
              ? "hidden"
              : "hidden custom-md:flex custom-md:flex-row text-custom-atrament custom-md:text-md xl:text-lg 3xl:text-xl space-x-2 custom-md:space-x-4 lg:space-x-6 xl:space-x-8 3xl:space-x-12 space-y-0"
          }`}
        >
          {items.map((item) => (
            <Link
              key={item.label}
              to={item.path}
              className={`py-2 ${
                selectedItem === item.path
                  ? "border-b-4 border-custom-red"
                  : "border-none"
              }`}
              onClick={() => {
                setSelectedItem(item.path);
                setIsOpen(false);
              }}
            >
              {item.label}
            </Link>
          ))}
        </div>

        {/* Get Quote Button */}
        <button
          onClick={openModal}
          className="hidden custom-md:flex h-[3rem] px-6 border-2 bg-custom-atrament rounded-lg items-center hover:border-custom-red hover:bg-custom-red hover:text-custom-white font-roboto text-custom-white px-4 py-2 transition duration-200 ease-in-out"
        >
          REZERVOVAŤ
        </button>

        <ModalComponent modalOpen={modalOpen} closeModal={closeModal} />
      </nav>
    </>
  );
};

export default MenuBar;
